<template>
  <DModal :show-close="false" :isBgOverlay="true" :placeInTop="true" @close="$emit('close')">
    <div class="d-modal" id="d-create-task-for-user" slot="insertComponent">
      <div class="d-modal__header">
        <h2>{{ $t('vue2.create_activity.create_activity_for') }} {{ user.name }}</h2>
      </div>
      <div class="d-modal__content">
        <div class="d-group">
          <label class="d-label">{{ $t('vue2.create_activity.title') }}</label>
          <div class="choose-subject">
            <dSelect :items="sortedSubjects"
                     :receiveData="theNewSubject"
                     :multiple="false"
                     :placeholder="$t('vue2.create_activity.search_or_create')"
                     :itemSpaceBetween="true"
                     @enter="onCreateNewSubject"
                     @update="setSubject"
                     @searchText="setTheSearchText"
                     @onSearchTextDebounced="onSearchTextDebounced"
                     @close="onCloseDSelect">
              <template v-for="(item, index) in sortedSubjects" v-slot:test="slotProps">
                <div class="choose-subject__event_type">
                  {{ slotProps.item.vSubjectType }}
                </div>
              </template>
              <div slot="noMatch">
                <h4 class="create-subject-link"
                    @click="onCreateNewSubject">
                  + {{ $t('vue2.substitution.create') }}: "{{ theSearchText }}"
                </h4>
              </div>
            </dSelect>
            <div class="icon__bg-circle create-new-subject"
                 v-if="isShowCreateSubjectButton"
                 v-tippy="{
                arrow: true,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window'
              }"
                 :content='$t("vue2.substitution.create") + ` "` + theSearchText + `" `'
                 @click="onCreateNewSubject">
              <span class="d-icon-plus"></span>
            </div>
          </div>
        </div>
        <div class="d-group">
          <div class="time-changer__group">
            <TimeChanger
              :showIcon="true"
              :start="startTime"
              :end="endTime"
              :isFiveMinutesInterval="false"
              @updateStart="setStartTime"
              @updateEnd="setEndTime">
            </TimeChanger>
          </div>
        </div>
        <div class="d-group">
          <DateSelector
            :date="date"
            @changed="setDate">
          </DateSelector>
        </div>
        <div class="d-group" v-if="showSubstituteOption">
          <div class="set-as-substitute" @click.stop="isSetAsSubstitute = !isSetAsSubstitute">
            <AppToggle :propOn="isSetAsSubstitute" :allowChanges="false"></AppToggle>
            <label class="d-label set-as-substitute__label">{{ $t('vue2.create_activity.set_as_substitute') }}</label>
            <div class="set-as-substitute__help"
                 @click.stop
                 :content="$t('vue2.create_activity.set_as_substitute_help')"
                 v-tippy="{
                    arrow: true,
                    animateFill: false,
                    animation: 'shift-away',
                    boundary: 'window',
                    placement: 'top',
                    zIndex: '999999992'
                  }">
              <span class="icon-question3 d-label__help"></span>
            </div>
          </div>
        </div>
      </div>
      <div class="d-modal__buttons pad-1">
        <button class="do-bttn" @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>
        <button class="do-bttn do-bttn--submit do-bttn--submit--disabled" v-if="!isValidated">{{
            $t('vue2.common.save')
          }}
        </button>
        <button class="do-bttn do-bttn--submit" v-else
                :class="{'do-bttn--submit--loading': isSubmitting}"
                @click="submit">{{ $t('vue2.common.save') }}
        </button>
      </div>
    </div>
  </DModal>
</template>

<script>
import docendo from '../../docendo'
import DModal from '../common/DModal'
import dSelect from '../DSelect/DSelect.vue'
import moment from 'moment'
import TimeChanger from '../TimeChanger'
import eventbus from '../../eventbus'
import DateSelector from '../datepicker/DateSelector.vue'
import AppToggle from '../common/AppToggle.vue'

export default {
  components: {dSelect, DModal, TimeChanger, DateSelector, AppToggle},
  props: {
    user: {
      type: Object,
      default: () => {
        return {
          name: 'Henrik',
          user_calendar_id: 7
        }
      }
    },
    date: {
      type: Object,
      default: () => moment()
    },
    theNewlyCreatedSubject: {
      type: Object,
      default: null
    },
    showSubstituteOption: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoadingSubject: false,
      subjects: [],
      startTime: moment().set({'hour': 8, 'minutes': 0}),
      endTime: moment().set({'hour': 8, 'minutes': 45}),
      selectedSubject: null,
      isSubmitting: false,
      theSearchText: '',
      theNewSubject: null,
      isShowCreateSubjectButton: false,
      isSetAsSubstitute: this.showSubstituteOption
    }
  },
  computed: {
    isValidated () {
      return this.selectedSubject
    },
    filteredSubjects () {
      return this.subjects.filter(subject => subject.subject_type !== 'absence')
    },
    sortedSubjects () {
      return this.filteredSubjects
        .map(subject => {
          // Try to get the translation for 'subject_type'
          const translatedSubjectType = this.$t(`vue2.eventTypes.${subject.subject_type}`)

          // Check if the translation was found or if it returned the same key
          const vSubjectType = translatedSubjectType === `vue2.eventTypes.${subject.subject_type}`
            ? subject.subject_type
            : translatedSubjectType
          // Return the subject with the new property 'v_subject_type'
          return {
            ...subject,
            vSubjectType
          }
        })
        .sort((a, b) => {
          // Sort alphabetically by the 'name' property
          if (a.name < b.name) return -1
          if (a.name > b.name) return 1
          return 0
        })
    },
    dbEventStart () {
      let date = moment(this.date).format('YYYY-MM-DD')
      let time = this.startTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    },
    dbEventEnd () {
      let date = moment(this.date).format('YYYY-MM-DD')
      let time = this.endTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    }
  },
  watch: {
    theNewlyCreatedSubject (val) {
      this.readSubjects()
    }
  },
  methods: {
    setDate (date) {
      this.$emit('setDate', date)
    },
    onCloseDSelect () {
      this.isShowCreateSubjectButton = false
    },
    setIsShowCreateSubjectButton () {
      const idx = this.filteredSubjects.findIndex(subject => subject.name === this.theSearchText)
      this.isShowCreateSubjectButton = this.theSearchText.length > 0 && idx < 0
    },
    onSearchTextDebounced () {
      this.setIsShowCreateSubjectButton()
    },
    onCreateNewSubject () {
      this.$emit('createNewSubject', this.theSearchText)
      this.theSearchText = ''
    },
    setTheSearchText (str) {
      this.theSearchText = str
    },
    setSubject (arr) {
      this.selectedSubject = arr[0]
    },
    setStartTime (payload) {
      this.startTime = payload
    },
    setEndTime (payload) {
      this.endTime = payload
    },
    readSubjects () {
      this.isLoadingSubject = true
      docendo.sidebar(this.user.user_calendar_id, this.date, success => {
        this.isLoadingSubject = false
        this.subjects = success
        this.$nextTick(_ => {
          if (this.theNewlyCreatedSubject) {
            this.theNewSubject = this.theNewlyCreatedSubject
          }
        })
      }, failure => {
        console.log('something went wrong')
      })
    },
    submit () {
      const schoolId = this.selectedSubject.school_id
      const calendarId = this.selectedSubject.calendar_id
      const calendarIds = [calendarId]
      const subjectId = this.selectedSubject.id
      const internalNote = ''
      this.isSubmitting = true
      const options = {}
      if (this.isSetAsSubstitute) {
        // The user is put on the event as a substitute without covering for anyone
        options.substituteCalendarIds = [this.user.user_calendar_id]
      }
      docendo.createEvent(schoolId, calendarId, calendarIds, subjectId, this.dbEventStart, this.dbEventEnd, '', '', '', internalNote, options, data => {
        if (data && data.status === 'error') {
          eventbus.toast = {text: data.message, 'success': false}
          this.$emit('close')
          return
        }
        const lesson = JSON.parse(data)
        this.$emit('onCreatedEvent', lesson)
        if (this.isSetAsSubstitute) {
          // Refresh the substitute pool hours shown in the scheduler
          this.$store.dispatch('getUserSubPools')
        }
        this.$emit('close')
        eventbus.toast = {text: this.$t('vue2.create_activity.created'), 'success': true}
      }, failure => {
        eventbus.toast = {text: 'Something went wrong', 'success': false}
      })
    }
  },
  mounted () {
    this.readSubjects()
    setTimeout(_ => {
      const el = document.querySelector('#d-create-task-for-user .d-select__input-look > input[type="search"]')
      if (el) {
        el.focus()
      }
    }, 400)
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.d-modal {
  width: 550px;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
}

.d-modal__close {
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 16px !important;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.d-modal__content {
  padding: 2rem 3rem;
}

.d-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.choose-subject {
  position: relative;
}

.create-new-subject {
  position: absolute;
  top: 0.2rem;
  right: -2.6rem;
}

.d-icon-plus {
  font-size: 13px;
}

.d-icon-plus:before {
  content: '+';
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  background-color: rgb(162, 163, 166);
  opacity: 0.6;
}

.create-subject-link {
  background-color: var(--blue) !important;
  color: white;
  cursor: pointer;
}

.set-as-substitute {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.set-as-substitute__label {
  margin-bottom: 0;
  cursor: pointer;
}

.set-as-substitute__help {
  display: flex;
  align-items: center;
}

.d-label__help {
  font-size: 12px;
  color: #8898aa;
  cursor: help;
}

.d-label__help:hover {
  color: var(--blue, #468ad7);
}

.choose-subject__event_type {
  padding: 0 0.55rem;
  font-style: italic;
  font-size: 10px;
  color: #afafb3;
  border-color: #999;
}

</style>



// WEBPACK FOOTER //
// CreateTaskForm.vue?1b3f3362